// src/pages/client/Home.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { useApp } from 'contexts/AppContext';
import { appointmentsService, notificationsService } from 'infrastructure/firebase';
import { sendAppointmentCancelled, sendAppointmentRescheduled } from 'infrastructure/emailService';
import { StatusBadge, Button, Modal, EmptyState } from 'shared/components/ui';
import { formatDate, formatCurrency } from 'shared/utils/helpers';
import { useMobile } from 'shared/hooks/useMobile';

function ConfirmCard({ notification, appointment, onConfirm, onCancel }) {
  const [loading, setLoading] = useState(false);

  const handle = async (action) => {
    setLoading(true);
    try { await action(); } finally { setLoading(false); }
  };

  return (
    <div style={{ background:'#fffbeb', border:'1.5px solid #fbbf24', borderRadius:'var(--radius-lg)', padding:'20px 22px', marginBottom:16 }}>
      <div style={{ display:'flex', alignItems:'flex-start', gap:14 }}>
        <div style={{ fontSize: 24, flexShrink:0 }}>⏰</div>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:700, fontSize:15, marginBottom:4, color:'#92400e' }}>
            Confirme sua presença
          </div>
          <div style={{ fontSize:14, color:'#78350f', marginBottom:12, lineHeight:1.5 }}>
            {notification.message}
          </div>
          {appointment && (
            <div style={{ background:'rgba(0,0,0,0.05)', borderRadius:8, padding:'10px 12px', marginBottom:14, fontSize:13 }}>
              <div><strong>Procedimento:</strong> {appointment.procedureName}</div>
              <div><strong>Profissional:</strong> {appointment.professionalName}</div>
              <div><strong>Data:</strong> {formatDate(appointment.date)} às {appointment.time}</div>
              <div><strong>Valor:</strong> {formatCurrency(appointment.price)}</div>
            </div>
          )}
          <div style={{ display:'flex', gap:10 }}>
            <Button variant="success" loading={loading} onClick={() => handle(onConfirm)}>✓ Confirmar presença</Button>
            <Button variant="danger" loading={loading} onClick={() => handle(onCancel)}>✗ Não poderei comparecer</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RescheduleCard({ notification, onAccept, onReject }) {
  const [acceptModal, setAcceptModal] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [message, setMessage]         = useState('');
  const [loading, setLoading]         = useState(false);
  const rd = notification.rescheduleData || {};

  const handle = async (action) => {
    setLoading(true);
    try { await action(); } finally { setLoading(false); }
  };

  return (
    <>
      <div style={{ background:'#eff6ff', border:'1.5px solid #93c5fd', borderRadius:'var(--radius-lg)', padding:'20px 22px', marginBottom:16 }}>
        <div style={{ display:'flex', alignItems:'flex-start', gap:14 }}>
          <div style={{ fontSize: 24, flexShrink:0 }}>🔄</div>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:700, fontSize:15, marginBottom:4, color:'#1e40af' }}>
              Solicitação de troca de horário
            </div>
            <div style={{ fontSize:14, color:'#1e3a8a', marginBottom:12, lineHeight:1.5 }}>
              {notification.message}
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <Button variant="success" onClick={() => setAcceptModal(true)}>✓ Aceitar troca</Button>
              <Button variant="danger"  onClick={() => setRejectModal(true)}>✗ Recusar</Button>
            </div>
          </div>
        </div>
      </div>

      {/* Accept modal */}
      <Modal open={acceptModal} onClose={() => setAcceptModal(false)} title="Aceitar Troca de Horário"
        footer={<>
          <Button variant="secondary" onClick={() => setAcceptModal(false)}>Cancelar</Button>
          <Button variant="success" loading={loading} onClick={() => handle(() => onAccept(message))}>✓ Confirmar Troca</Button>
        </>}>
        <p style={{ marginBottom:16 }}>Você está aceitando a troca de horário para <strong>{formatDate(rd.newDate)} às {rd.newTime}</strong>.</p>
        <div className="form-group">
          <label className="form-label">Mensagem (opcional)</label>
          <textarea className="form-input" rows={3} value={message} onChange={e=>setMessage(e.target.value)} placeholder="Ok, aceito a troca!" />
        </div>
      </Modal>

      {/* Reject modal */}
      <Modal open={rejectModal} onClose={() => setRejectModal(false)} title="Recusar Troca de Horário"
        footer={<>
          <Button variant="secondary" onClick={() => setRejectModal(false)}>Cancelar</Button>
          <Button variant="danger" loading={loading} onClick={() => handle(() => onReject(message))}>✗ Confirmar Recusa</Button>
        </>}>
        <p style={{ marginBottom:16 }}>Você está recusando a troca. O horário original será mantido.</p>
        <div className="form-group">
          <label className="form-label">Motivo (opcional)</label>
          <textarea className="form-input" rows={3} value={message} onChange={e=>setMessage(e.target.value)} placeholder="Não consigo neste novo horário..." />
        </div>
      </Modal>
    </>
  );
}

export default function ClientHome() {
  const isMobile = useMobile();
  const { userProfile } = useAuth();
  const { myAppointments: appointments, notifications, showToast } = useApp();
  const navigate = useNavigate();

  const _n = new Date();
  const today = `${_n.getFullYear()}-${String(_n.getMonth()+1).padStart(2,'0')}-${String(_n.getDate()).padStart(2,'0')}`;
  const upcomingAppts = appointments
    .filter(a => a.date >= today && a.status !== 'cancelled')
    .sort((a,b) => a.date > b.date ? 1 : a.time > b.time ? 1 : -1)
    .slice(0, 3);

  const confirmNotifs = notifications.filter(n => n.type === 'confirm');
  const rescheduleNotifs = notifications.filter(n => n.type === 'reschedule');

  // Confirm appointment
  const handleConfirm = async (notif) => {
    try {
      // Tenta encontrar o agendamento pelo ID salvo na notificação
      const apptId = notif.appointmentId;
      const appt   = apptId
        ? appointments.find(a => a.id === apptId)
        // fallback legado: busca por data/hora da notificação
        : appointments.find(a =>
            a.date === notif.appointmentDate &&
            a.time === notif.appointmentTime &&
            a.clientId === userProfile?.id
          );
      if (!appt) {
        // Agendamento não encontrado — descarta a notificação solta
        await notificationsService.dismiss(notif.id);
        showToast('Notificação desatualizada. Verifique sua agenda.', 'warning');
        return;
      }
      await appointmentsService.confirm(appt.id);
      await notificationsService.dismiss(notif.id);
      showToast('Presença confirmada! Até logo. 🎉');
    } catch (error) {
      console.error('[Confirm]', error);
      showToast('Erro ao confirmar presença.', 'danger');
    }
  };

  const handleCancelAppt = async (notif) => {
    try {
      const apptId = notif.appointmentId;
      const appt   = apptId
        ? appointments.find(a => a.id === apptId)
        : appointments.find(a =>
            a.date === notif.appointmentDate &&
            a.time === notif.appointmentTime &&
            a.clientId === userProfile?.id
          );
      if (!appt) {
        await notificationsService.dismiss(notif.id);
        showToast('Notificação desatualizada. Verifique sua agenda.', 'warning');
        return;
      }
      await appointmentsService.cancel(appt.id);
      if (userProfile?.email) {
        sendAppointmentCancelled({
          clientName:       userProfile.name,
          clientEmail:      userProfile.email,
          professionalName: appt.professionalName,
          procedureName:    appt.procedureName,
          date:             appt.date,
          time:             appt.time,
        }).catch(err => console.warn('[Email] Falha ao enviar cancelamento:', err));
      }
      await notificationsService.dismiss(notif.id);
      showToast('Agendamento cancelado. O horário ficará disponível para outro cliente.');
    } catch (error) {
      showToast('Erro ao cancelar agendamento.', 'danger');
    }
  };

  // Accept reschedule
  const handleAcceptReschedule = async (notif, message) => {
    try {
      const rd = notif.rescheduleData || {};
      if (rd.appointmentId) {
        await appointmentsService.update(rd.appointmentId, {
          date: rd.newDate, time: rd.newTime, status: 'confirmed'
        });
        if (userProfile?.email) {
          sendAppointmentRescheduled({
            clientName:       userProfile.name,
            clientEmail:      userProfile.email,
            professionalName: rd.professionalName || '',
            procedureName:    rd.procedureName || '',
            oldDate:          rd.oldDate,
            oldTime:          rd.oldTime,
            newDate:          rd.newDate,
            newTime:          rd.newTime,
          }).catch(err => console.warn('[Email] Falha ao enviar reagendamento:', err));
        }
      }
      await notificationsService.dismiss(notif.id);
      showToast('Troca de horário aceita! Seu agendamento foi atualizado.');
    } catch (error) {
      showToast('Erro ao aceitar troca.', 'danger');
    }
  };

  const handleRejectReschedule = async (notif, message) => {
    try {
      await notificationsService.dismiss(notif.id);
      showToast('Troca recusada. Seu horário original foi mantido.');
    } catch (error) {
      showToast('Erro ao recusar troca.', 'danger');
    }
  };

  return (
    <div style={{ maxWidth: 680 }}>
      {/* Greeting */}
      <div style={{ background:'linear-gradient(135deg,#1a1a2e,#16213e)', borderRadius:'var(--radius-lg)', padding: isMobile ? '16px 18px' : '24px 28px', color:'#fff', marginBottom:24 }}>
        <div style={{ fontFamily:"'Instrument Serif',serif", fontSize: 24 }}>
          Olá, {userProfile?.name?.split(' ')[0]}! 👋
        </div>
        <div style={{ opacity:0.7, marginTop:4, fontSize:14 }}>
          {notifications.length > 0
            ? `Você tem ${notifications.length} pendência(s) para resolver`
            : 'Tudo em dia! Nenhuma pendência.'}
        </div>
      </div>

      {/* Confirm notifications */}
      {confirmNotifs.map(n => {
        // Mesmo fallback dos handlers: por ID → ou por data/hora/clientId
        const appt = n.appointmentId
          ? appointments.find(a => a.id === n.appointmentId)
          : appointments.find(a =>
              a.date === n.appointmentDate &&
              a.time === n.appointmentTime &&
              a.clientId === userProfile?.id
            );
        return (
          <ConfirmCard key={n.id} notification={n} appointment={appt}
            onConfirm={() => handleConfirm(n)}
            onCancel={() => handleCancelAppt(n)} />
        );
      })}

      {/* Reschedule notifications */}
      {rescheduleNotifs.map(n => (
        <RescheduleCard key={n.id} notification={n}
          onAccept={(msg) => handleAcceptReschedule(n, msg)}
          onReject={(msg) => handleRejectReschedule(n, msg)} />
      ))}

      {/* Upcoming appointments */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">Próximos Agendamentos</div>
          <Button variant="secondary" size="sm" onClick={() => navigate('/client/agenda')}>Ver todos →</Button>
        </div>
        <div>
          {upcomingAppts.length === 0 ? (
            <EmptyState icon="📅" title="Sem agendamentos" text="Você não tem agendamentos futuros."
              action={<Button variant="primary" onClick={() => navigate('/client/catalog')}>+ Fazer Agendamento</Button>} />
          ) : (
            upcomingAppts.map(a => (
              <div key={a.id} style={{ padding:'16px 20px', display:'flex', alignItems:'center', gap:14, borderBottom:'1px solid var(--border)' }}>
                <div style={{ width:50, textAlign:'center', background:'var(--surface-2)', borderRadius:10, padding:'8px 4px', flexShrink:0 }}>
                  <div style={{ fontSize:20, fontWeight:700, color:'var(--accent)', lineHeight:1 }}>{a.date?.split('-')[2]}</div>
                  <div style={{ fontSize:10, color:'var(--text-muted)', textTransform:'uppercase' }}>
                    {['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'][Number(a.date?.split('-')[1])-1]}
                  </div>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, fontSize:14 }}>{a.procedureName}</div>
                  <div style={{ fontSize:12, color:'var(--text-muted)' }}>
                    {a.professionalName} • {a.time} • {formatCurrency(a.price)}
                  </div>
                </div>
                <StatusBadge status={a.status} />
              </div>
            ))
          )}
        </div>
        <div style={{ padding:'16px 20px', borderTop:'1px solid var(--border)' }}>
          <Button variant="primary" style={{ width:'100%' }} onClick={() => navigate('/client/catalog')}>
            + Fazer Novo Agendamento
          </Button>
        </div>
      </div>
    </div>
  );
}
